import React, { useMemo, useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import styled from 'styled-components';
import Spinner from "./Spinner";

// Hook to detect mobile screen size
const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);

        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    return isMobile;
};

// Mobile card component for responsive display (copied from MainTableV2)
const MobileCard = ({ row, columns, index }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const getDisplayValue = (column, row) => {
        if (column.cell) {
            return column.cell(row, index);
        }
        return column.selector ? column.selector(row) : '';
    };

    const getFirstColumn = () => columns.find(col => !col.hide || col.hide !== 'sm');
    const getOtherColumns = () => columns.filter(col => col !== getFirstColumn());

    const firstColumn = getFirstColumn();
    const otherColumns = getOtherColumns();

    return (
        <div className="mobile-card">
            <div
                className="mobile-card-header"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div>
                    <div className="font-semibold text-gray-900">
                        {firstColumn ? getDisplayValue(firstColumn, row) : 'No data'}
                    </div>
                    {otherColumns.slice(0, 1).map(column => (
                        <div key={column.name} className="text-sm text-gray-500 mt-1">
                            {column.name}: {getDisplayValue(column, row)}
                        </div>
                    ))}
                </div>
                <div className="flex items-center">
                    <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </div>

            <div className={`mobile-card-content ${isExpanded ? 'expanded' : ''}`}>
                {otherColumns.slice(1).map(column => (
                    <div key={column.name} className="mobile-card-field">
                        <span className="mobile-card-label">{column.name}</span>
                        <span className="mobile-card-value">
                            {getDisplayValue(column, row)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const StyledDataTable = styled(DataTable)`
    .rdt_Table {
        border-radius: 8px;
        overflow: hidden;
    }

    .rdt_TableRow {
        border-bottom: 1px solid #f1f5f9;
        transition: background-color 0.15s ease;
    }

    .rdt_TableRow:hover {
        background-color: #f8fafc;
    }

    .rdt_TableHeadRow {
        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        border-bottom: 1px solid #e2e8f0;
        position: relative;
        font-weight: 600;
        color: #374151;
    }

    .rdt_TableHeadRow::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
        opacity: 0.8;
    }

    .rdt_TableHeader {
        font-size: 0.875rem;
        font-weight: 600;
        color: #374151;
        padding: 16px 12px;
    }

    .rdt_TableCell {
        padding: 12px;
        font-size: 0.875rem;
        color: #1f2937;
    }
`;

// Pagination component
const PaginationControls = ({
    pagination,
    onPageChange,
    onPageSizeChange,
    loading,
    pageSizeOptions = [25, 50, 100]
}) => {
    if (!pagination) return null;

    const { currentPage, totalPages, totalCount, pageSize, hasNextPage, hasPreviousPage } = pagination;

    const startRecord = ((currentPage - 1) * pageSize) + 1;
    const endRecord = Math.min(currentPage * pageSize, totalCount);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && !loading) {
            onPageChange(newPage);
        }
    };

    const handlePageSizeChange = (newPageSize) => {
        if (!loading) {
            onPageSizeChange(parseInt(newPageSize));
        }
    };

    // Generate page numbers to show
    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

        // Adjust if we're near the end
        if (endPage - startPage + 1 < maxPagesToShow) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    };

    return (
        <div className="bg-gray-50 border-t border-gray-200 px-4 py-3 flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
                {/* Mobile pagination */}
                <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!hasPreviousPage || loading}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Previous
                </button>
                <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!hasNextPage || loading}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Next
                </button>
            </div>

            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center space-x-4">
                    <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{startRecord}</span> to{' '}
                        <span className="font-medium">{endRecord}</span> of{' '}
                        <span className="font-medium">{totalCount}</span> results
                    </p>

                    <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-700">Show:</label>
                        <select
                            value={pageSize}
                            onChange={(e) => handlePageSizeChange(e.target.value)}
                            disabled={loading}
                            className="border border-gray-300 rounded-md text-sm px-2 py-1 bg-white disabled:opacity-50"
                        >
                            {pageSizeOptions.map(size => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </select>
                        <span className="text-sm text-gray-700">per page</span>
                    </div>
                </div>

                <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={!hasPreviousPage || loading}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="sr-only">Previous</span>
                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </button>

                        {getPageNumbers().map(pageNum => (
                            <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                disabled={loading}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium disabled:cursor-not-allowed ${
                                    pageNum === currentPage
                                        ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                            >
                                {pageNum}
                            </button>
                        ))}

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={!hasNextPage || loading}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="sr-only">Next</span>
                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </nav>
                </div>
            </div>
        </div>
    );
};

const ServerPaginatedTable = ({
    columns,
    data,
    primaryColor,
    loading,
    alertMessage,
    clearAlert,
    title,
    children,
    dashboard,
    pagination,
    onPageChange,
    onPageSizeChange,
    onSort,
    pageSizeOptions = [25, 50, 100]
}) => {
    const isMobile = useIsMobile();

    // Enhanced columns with sorting handlers
    const enhancedColumns = useMemo(() => {
        return columns.map(column => ({
            ...column,
            sortable: column.sortable && onSort ? true : false
        }));
    }, [columns, onSort]);

    // Handle sort events from DataTable
    const handleSort = (column, sortDirection) => {
        if (onSort && column.sortField) {
            const direction = sortDirection === 'asc' ? 'ASC' : 'DESC';
            onSort(column.sortField, direction);
        }
    };

    return (
        <>
            {loading && (
                <div className="fixed inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75">
                    <Spinner />
                </div>
            )}
            {alertMessage && (
                <div
                    className={`alert p-4 rounded-lg mb-4 ${
                        alertMessage.startsWith("Successfully")
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : "bg-red-50 text-red-700 border border-red-200"
                    } flex justify-between items-center shadow-sm`}
                >
                    {alertMessage}
                    <button onClick={clearAlert} className="text-lg font-bold hover:opacity-70">
                        ×
                    </button>
                </div>
            )}

            <div className="space-y-6">
                {/* Header Section - Only show if title is provided */}
                {title && (
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
                    </div>
                )}

                {/* Dashboard Section - Full width for analytics/complex content */}
                {dashboard && (
                    <div className="w-full dashboard-section">
                        {dashboard}
                    </div>
                )}

                {/* Controls Bar - Action Buttons */}
                {children && (
                    <div className="controls-bar">
                        <div className="controls-actions">
                            {children}
                        </div>
                    </div>
                )}

                {/* Data Display Section */}
                <div className="table-card">
                    {isMobile ? (
                        // Mobile Card View
                        <div className="p-4">
                            {data.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p className="text-sm">No data available</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {data.map((row, index) => (
                                        <MobileCard
                                            key={index}
                                            row={row}
                                            columns={columns}
                                            index={index}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        // Desktop Table View
                        <StyledDataTable
                            columns={enhancedColumns}
                            data={data}
                            primaryColor={primaryColor}
                            responsive={false}
                            pagination={false} // Disable built-in pagination
                            persistTableHead
                            highlightOnHover
                            noHeader={false}
                            sortServer={true}
                            onSort={handleSort}
                            customStyles={{
                                table: {
                                    style: {
                                        backgroundColor: 'white',
                                    },
                                },
                                headCells: {
                                    style: {
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        color: '#374151',
                                        paddingLeft: '12px',
                                        paddingRight: '12px',
                                    },
                                },
                                cells: {
                                    style: {
                                        fontSize: '0.875rem',
                                        color: '#1f2937',
                                        paddingLeft: '12px',
                                        paddingRight: '12px',
                                    },
                                },
                            }}
                        />
                    )}

                    {/* Custom Server-Side Pagination */}
                    <PaginationControls
                        pagination={pagination}
                        onPageChange={onPageChange}
                        onPageSizeChange={onPageSizeChange}
                        loading={loading}
                        pageSizeOptions={pageSizeOptions}
                    />
                </div>
            </div>
        </>
    );
};

export default ServerPaginatedTable;